import React from "react";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import { events } from "@planzone/events";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveDocuments,
  useRetrievePrivateDocuments,
  useUploadDocument,
} from "@planzone/view-models";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import { SelectList } from "@common/selectList";
import { Loader } from "@common/loader";
import { useMessage } from "@components/useMessage";
import { Props } from "./types";
import { DirectoryStructure } from "./directoryStructure";
import { Image } from "./image";
import { AddIcon } from "./addIcon";
import { FileInput } from "./fileInput";
import styles from "./styles.scss";

function DocumentForm({ value, readOnly, disabled, flexible, submit }: Props) {
  const [docId, setDocId] = React.useState(uuid());
  const ctx = useWorkspaceContext();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { folder } = useRetrievePrivateDocuments(
    ctx.type === "project" ? ctx.project.uuid : planzone.uuid,
    ctx.type === "project" ? "project" : "planzone",
  );
  const { folder: rootFolder, isRequestPending: isLoading } =
    useRetrieveDocuments(
      ctx.type === "project" ? ctx.project.uuid : planzone.uuid,
      ctx.type === "project" ? "project" : "planzone",
    );
  const { isRequestPending, uploadDocument } = useUploadDocument(folder);

  useMessage((event) => {
    if (
      event.type === events.uploadDocumentSucceeded &&
      event.data.document.name === docId
    ) {
      submit(event.data.document.uuid);
      setDocId(uuid());
    }
  });

  return readOnly ? (
    <div className={classNames(styles.documentForm, styles.readOnly)}>
      <Image value={value} className={styles.image} />
    </div>
  ) : (
    <SelectList
      inputClassName={styles.documentForm}
      listClassName={styles.listRoot}
    >
      {({}) => ({
        input: (
          <div
            className={classNames({
              [styles.disabled]: disabled,
            })}
          >
            {value ? (
              <Image
                value={value}
                className={classNames(styles.image, {
                  [styles.flexible]: flexible,
                })}
              />
            ) : (
              <AddIcon className={styles.addIcon} />
            )}
          </div>
        ),
        list: !disabled && (
          <div className={styles.list}>
            {(rootFolder.children.length > 0 || isLoading) && (
              <div className={styles.directoryStructure}>
                <DirectoryStructure select={({ uuid }) => submit(uuid)} />
              </div>
            )}
            <div className={styles.footer}>
              {isRequestPending ? (
                <div className={styles.loader}>
                  <Loader />
                </div>
              ) : (
                <FileInput
                  input={(file) =>
                    ctx.type === "project"
                      ? uploadDocument({
                          namespace: ctx.project.uuid,
                          type: "project",
                          folder,
                          file: { ...file, name: docId },
                        })
                      : uploadDocument({
                          namespace: planzone.uuid,
                          type: "planzone",
                          folder,
                          file: { ...file, name: docId },
                        })
                  }
                />
              )}
            </div>
          </div>
        ),
      })}
    </SelectList>
  );
}

export { DocumentForm };
